/**
 * Bilingual content for the four internal request forms.
 *
 * Source of truth: the two PDFs provided by the content team:
 *   - Arabic: “نماذج الطلبات الداخلية العربية لموقع SAS”
 *   - English: “Internal Request Forms for the SAS Website”
 *
 * The wording below is preserved verbatim. Machine-friendly option keys
 * (e.g. "training") are kept in sync with the backend whitelist in
 * `backend/requests_api.py` — do not change keys here without updating both.
 */

/* Shared “preferred reply method” field content (identical across all 4 forms). */
export const REPLY_METHOD_OPTIONS = [
  {
    key: "whatsapp",
    ar: "رسالة عبر واتساب",
    en: "WhatsApp message",
  },
  {
    key: "phone",
    ar: "مكالمة هاتفية",
    en: "Phone call",
  },
  {
    key: "email",
    ar: "بريد إلكتروني",
    en: "Email",
  },
];

export const REPLY_METHOD_LABEL = {
  ar: "طريقة الرد المفضلة",
  en: "Preferred reply method",
};

/* The success message shown after every successful submission. */
export const SUCCESS_MESSAGE = {
  ar: "تم تسجيل طلبكم بنجاح، وسيتم التواصل معكم في أقرب وقت.",
  en: "Your request has been submitted successfully. Our team will contact you as soon as possible.",
};

export const SUCCESS_TITLE = {
  ar: "تم إرسال طلبكم",
  en: "Request sent",
};

export const GENERIC_STRINGS = {
  ar: {
    submit: "إرسال الطلب",
    submitting: "جارٍ الإرسال…",
    required: "حقل مطلوب",
    invalidEmail: "البريد الإلكتروني غير صحيح",
    tooShort: "النص قصير جداً",
    tooLong: "النص طويل جداً",
    selectPlaceholder: "— اختر —",
    optional: "(اختياري)",
    errorSummary: "الرجاء تصحيح الحقول المطلوبة أدناه.",
    serverError: "تعذّر إرسال طلبكم. الرجاء المحاولة لاحقاً.",
    rateLimited: "وصلتنا محاولات كثيرة من شبكتكم خلال فترة قصيرة. الرجاء الانتظار بضع دقائق ثم إعادة المحاولة.",
    sendAnother: "إرسال طلب آخر",
    backToContact: "العودة إلى صفحة التواصل",
    contactBreadcrumb: "تواصل",
  },
  en: {
    submit: "Submit request",
    submitting: "Submitting…",
    required: "Required field",
    invalidEmail: "Please enter a valid email address",
    tooShort: "This value is too short",
    tooLong: "This value is too long",
    selectPlaceholder: "— Select —",
    optional: "(optional)",
    errorSummary: "Please correct the highlighted fields below.",
    serverError: "We couldn’t send your request. Please try again later.",
    rateLimited: "We’ve received several submissions from your network recently. Please wait a few minutes and try again.",
    sendAnother: "Send another request",
    backToContact: "Back to contact page",
    contactBreadcrumb: "Contact",
  },
};

/* ---------------------------------------------------------------------- */
/* 1) Partnership & collaboration — Admin: Partnership Requests          */
/* ---------------------------------------------------------------------- */
export const PARTNERSHIP_FORM = {
  key: "partnership",
  slug: "partnership",
  eyebrow: { ar: "تواصل", en: "Contact" },
  title: {
    ar: "طلب تعاون أو شراكة",
    en: "Partnership and Collaboration Request",
  },
  intro: {
    ar: "إذا كنتم تمثلون جامعة أو مدرسة أو منظمة أو مبادرة، يمكنكم إرسال طلب تعاون أو شراكة عبر هذا النموذج وسيتواصل معكم فريق SAS في أقرب وقت.",
    en: "If you represent a university, school, organisation, or initiative, you can use this form to submit a collaboration or partnership request and the SAS team will get back to you soon.",
  },
  adminLabel: "Partnership Requests",
  fields: [
    {
      name: "full_name",
      type: "text",
      required: true,
      label: { ar: "الاسم الكامل", en: "Full name" },
      autoComplete: "name",
    },
    {
      name: "organisation_name",
      type: "text",
      required: true,
      label: { ar: "اسم الجهة أو المؤسسة", en: "Organisation or institution name" },
      autoComplete: "organization",
    },
    {
      name: "email",
      type: "email",
      required: true,
      label: { ar: "البريد الإلكتروني", en: "Email address" },
      autoComplete: "email",
      dirLtr: true,
    },
    {
      name: "collaboration_type",
      type: "select",
      required: true,
      label: { ar: "نوع التعاون المطلوب", en: "Type of collaboration requested" },
      options: [
        { key: "training",   ar: "تعاون تدريبي",        en: "Training collaboration" },
        { key: "media",      ar: "تعاون إعلامي",        en: "Media collaboration" },
        { key: "technical",  ar: "تعاون تقني",          en: "Technical collaboration" },
        { key: "community",  ar: "تعاون مجتمعي",       en: "Community collaboration" },
        { key: "long_term",  ar: "شراكة طويلة المدى",   en: "Long-term partnership" },
        { key: "other",      ar: "أخرى",                en: "Other" },
      ],
    },
    {
      name: "description",
      type: "textarea",
      required: true,
      label: {
        ar: "وصف مختصر للطلب أو فكرة التعاون",
        en: "Short description of the request or collaboration idea",
      },
      rows: 5,
      minLength: 10,
    },
  ],
};

/* ---------------------------------------------------------------------- */
/* 2) Host a workshop — Admin: Workshop Hosting Requests                 */
/* ---------------------------------------------------------------------- */
export const WORKSHOP_FORM = {
  key: "workshop",
  slug: "host-workshop",
  eyebrow: { ar: "تواصل", en: "Contact" },
  title: {
    ar: "طلب استضافة ورشة",
    en: "Host a Workshop",
  },
  intro: {
    ar: "إذا كنتم ترغبون باستضافة ورشة تعريفية أو تطبيقية في مجال الذكاء الاصطناعي داخل جامعتكم أو مؤسستكم أو مركزكم، يمكنكم إرسال الطلب عبر هذا النموذج.",
    en: "If you would like to host an introductory or practical AI workshop inside your university, institution, or training centre, you can submit your request through this form.",
  },
  adminLabel: "Workshop Hosting Requests",
  fields: [
    {
      name: "full_name",
      type: "text",
      required: true,
      label: { ar: "الاسم الكامل", en: "Full name" },
      autoComplete: "name",
    },
    {
      name: "host_entity",
      type: "text",
      required: true,
      label: {
        ar: "اسم الجهة المستضيفة",
        en: "Host institution or organisation name",
      },
      autoComplete: "organization",
    },
    {
      name: "phone_or_whatsapp",
      type: "tel",
      required: true,
      label: {
        ar: "رقم الهاتف أو واتساب",
        en: "Phone number or WhatsApp",
      },
      autoComplete: "tel",
      dirLtr: true,
    },
    {
      name: "audience",
      type: "select",
      required: true,
      label: {
        ar: "الفئة المستهدفة أو نوع الورشة المطلوبة",
        en: "Target audience or type of workshop requested",
      },
      options: [
        { key: "university_students",  ar: "طلاب جامعة",                en: "University students" },
        { key: "school_students",      ar: "طلاب مدرسة",                en: "School students" },
        { key: "teachers_trainers",    ar: "معلمون أو مدربون",         en: "Teachers or trainers" },
        { key: "researchers",          ar: "باحثون",                     en: "Researchers" },
        { key: "general_public",       ar: "جمهور عام",                   en: "General public" },
        { key: "organisation_or_team", ar: "منظمة أو فريق عمل",         en: "Organisation or work team" },
        { key: "general_introductory", ar: "ورشة تعريفية عامة",        en: "General introductory workshop" },
        { key: "applied_workshop",     ar: "ورشة تطبيقية",              en: "Practical workshop" },
        { key: "training_series",      ar: "سلسلة تدريبية",              en: "Training series" },
      ],
    },
    {
      name: "notes",
      type: "textarea",
      required: false,
      label: {
        ar: "ملاحظات إضافية حول الطلب",
        en: "Additional notes about the request",
      },
      rows: 4,
    },
  ],
};

/* ---------------------------------------------------------------------- */
/* 3) General contact — Admin: General Contact Requests                  */
/* ---------------------------------------------------------------------- */
export const GENERAL_CONTACT_FORM = {
  key: "contact",
  slug: "general",
  eyebrow: { ar: "تواصل", en: "Contact" },
  title: {
    ar: "تواصل عام",
    en: "General Contact",
  },
  intro: {
    ar: "يمكنكم استخدام هذا النموذج للتواصل العام مع فريق SAS بشأن سؤال أو استفسار أو ملاحظة أو طلب معلومات إضافية.",
    en: "You can use this form to contact the SAS team regarding a question, request, suggestion, note, or general inquiry.",
  },
  adminLabel: "General Contact Requests",
  fields: [
    {
      name: "full_name",
      type: "text",
      required: true,
      label: { ar: "الاسم الكامل", en: "Full name" },
      autoComplete: "name",
    },
    {
      name: "phone_or_email",
      type: "text",
      required: true,
      label: {
        ar: "رقم الهاتف أو البريد الإلكتروني",
        en: "Phone number or email address",
      },
      dirLtr: true,
    },
    {
      name: "subject",
      type: "select",
      required: true,
      label: { ar: "موضوع الرسالة", en: "Message subject" },
      options: [
        { key: "general_inquiry",     ar: "استفسار عام",      en: "General inquiry" },
        { key: "information_request", ar: "طلب معلومات",    en: "Information request" },
        { key: "suggestion_note",     ar: "ملاحظة أو اقتراح", en: "Suggestion or note" },
        { key: "website_issue",       ar: "مشكلة في الموقع",    en: "Website issue" },
        { key: "workshop_question",   ar: "سؤال عن ورشة",      en: "Workshop question" },
        { key: "tools_question",      ar: "سؤال عن الأدوات",   en: "Tools question" },
        { key: "other",               ar: "أخرى",             en: "Other" },
      ],
    },
    {
      name: "message",
      type: "textarea",
      required: true,
      label: {
        ar: "نص مختصر للطلب أو السؤال",
        en: "Short message or request",
      },
      rows: 5,
      minLength: 5,
    },
    {
      name: "city_or_entity",
      type: "text",
      required: false,
      label: { ar: "المدينة أو الجهة", en: "City or organisation" },
    },
  ],
};

/* ---------------------------------------------------------------------- */
/* 4) Join as contributor or volunteer — Admin: Volunteer Requests        */
/* ---------------------------------------------------------------------- */
export const VOLUNTEER_FORM = {
  key: "volunteer",
  slug: "volunteer",
  eyebrow: { ar: "تواصل", en: "Contact" },
  title: {
    ar: "الانضمام كمساهم أو متطوع",
    en: "Join as a Contributor or Volunteer",
  },
  intro: {
    ar: "إذا كنتم ترغبون بالمساهمة في أنشطة SAS أو دعم المبادرة بمهاراتكم أو وقتكم أو خبراتكم، يمكنكم إرسال هذا النموذج.",
    en: "If you would like to support SAS through your skills, time, or experience, you can use this form to express your interest in contributing or volunteering.",
  },
  adminLabel: "Volunteer Requests",
  fields: [
    {
      name: "full_name",
      type: "text",
      required: true,
      label: { ar: "الاسم الكامل", en: "Full name" },
      autoComplete: "name",
    },
    {
      name: "phone_or_email",
      type: "text",
      required: true,
      label: {
        ar: "رقم الهاتف أو البريد الإلكتروني",
        en: "Phone number or email address",
      },
      dirLtr: true,
    },
    {
      name: "contribution_area",
      type: "select",
      required: true,
      label: {
        ar: "المجال الذي ترغب بالمساهمة فيه",
        en: "Area you would like to contribute in",
      },
      options: [
        { key: "training",               ar: "التدريب",            en: "Training" },
        { key: "content_creation",       ar: "صناعة المحتوى",       en: "Content creation" },
        { key: "design",                 ar: "التصميم",             en: "Design" },
        { key: "programming",            ar: "البرمجة",              en: "Programming" },
        { key: "translation",            ar: "الترجمة",              en: "Translation" },
        { key: "coordination",           ar: "التنظيم والتنسيق",     en: "Coordination and organisation" },
        { key: "partnerships_outreach", ar: "العلاقات والشراكات", en: "Partnerships and outreach" },
        { key: "community_support",      ar: "الدعم المجتمعي",        en: "Community support" },
        { key: "other",                  ar: "أخرى",                  en: "Other" },
      ],
    },
    {
      name: "bio",
      type: "textarea",
      required: true,
      label: {
        ar: "نبذة قصيرة عن خبرتك أو اهتمامك",
        en: "Short note about your experience or interest",
      },
      rows: 5,
      minLength: 5,
    },
    {
      name: "city_or_participation",
      type: "text",
      required: false,
      label: {
        ar: "المدينة أو طريقة المشاركة المتاحة",
        en: "City or available participation mode",
      },
    },
  ],
};

/* ---------------------------------------------------------------------- */
/* 5) Support request — Admin: Support Requests                           */
/* ---------------------------------------------------------------------- */
export const SUPPORT_FORM = {
  key: "support",
  slug: "request",
  eyebrow: { ar: "الدعم", en: "Support" },
  title: {
    ar: "طلب دعم",
    en: "Request Support",
  },
  intro: {
    ar:
      "إذا واجهتك مشكلة تقنية في الموقع، أو لديك سؤال حول الأدوات أو الورشات، أو تحتاج إلى مساعدة في حسابك، يمكنك إرسال طلب دعم عبر هذا النموذج، وسيتواصل معك فريق SAS قريبًا.",
    en:
      "If you ran into a technical issue, have a question about the tools or workshops, or need help with your account, send us a support request through this form and the SAS team will reach out shortly.",
  },
  adminLabel: "Support Requests",
  fields: [
    {
      name: "full_name",
      type: "text",
      required: true,
      label: { ar: "الاسم الكامل", en: "Full name" },
      autoComplete: "name",
    },
    {
      name: "phone_or_email",
      type: "text",
      required: true,
      label: {
        ar: "البريد الإلكتروني أو رقم واتساب",
        en: "Email address or WhatsApp number",
      },
      autoComplete: "email",
      dirLtr: true,
    },
    {
      name: "issue_type",
      type: "select",
      required: true,
      label: { ar: "نوع المشكلة أو الطلب", en: "Issue type" },
      options: [
        { key: "technical_website",  ar: "مشكلة تقنية في الموقع",        en: "Technical website issue" },
        { key: "tools_question",     ar: "سؤال حول الأدوات",              en: "Question about the tools" },
        { key: "workshops_question", ar: "سؤال حول الورشات",              en: "Question about the workshops" },
        { key: "account_signin",     ar: "مشكلة في الحساب أو تسجيل الدخول", en: "Account or sign-in issue" },
        { key: "other",              ar: "أخرى",                            en: "Other" },
      ],
    },
    {
      name: "description",
      type: "textarea",
      required: true,
      label: {
        ar: "وصف مختصر للمشكلة أو الطلب",
        en: "Short description of the issue or request",
      },
      rows: 5,
      minLength: 10,
    },
  ],
};

/* ---------------------------------------------------------------------- */
/* 6) SAS V2 community feedback — Admin: V2 Feedback (Phase 24)           */
/* ---------------------------------------------------------------------- */
export const V2_FEEDBACK_FORM = {
  key: "v2_feedback",
  slug: "v2-feedback",
  eyebrow: { ar: "SAS V2", en: "SAS V2" },
  title: {
    ar: "قدّم اقتراحًا لـ SAS V2",
    en: "Submit a suggestion for SAS V2",
  },
  intro: {
    ar: "أخبرنا ما الذي ترغب في رؤيته في الإصدار القادم. قد يساهم اقتراحك مباشرة في تحديد ما نبنيه لاحقاً.",
    en: "Tell us what you would like to see in the next version. Your suggestion can influence what we build next.",
  },
  adminLabel: "V2 Feedback",
  // Phase 24 spec flags — see RequestForm.jsx header for semantics.
  contactOptional: true,
  contactFieldNames: ["contact_name", "contact_reach"],
  suggestionCategoryField: "suggestion_category",
  eventNames: { submit: "v2_feedback_submit" },
  submitLabel: {
    ar: "أرسل اقتراحي",
    en: "Send my suggestion",
  },
  successMessage: {
    ar: "شكراً لك. تم استلام اقتراحك وسيتم مراجعته ضمن عملية تطوير SAS V2.",
    en: "Thank you. Your suggestion has been received and will be reviewed as part of the SAS V2 planning process.",
  },
  fields: [
    {
      name: "suggestion_category",
      type: "select",
      required: true,
      label: {
        ar: "ما نوع الاقتراح؟",
        en: "What would you like to suggest?",
      },
      // Keys mirror the backend whitelist _V2_SUGGESTION_CATEGORIES in
      // requests_api.py. Order matches the spec sheet.
      options: [
        { key: "new_tool",               ar: "أداة ذكاء اصطناعي جديدة",  en: "New AI tool" },
        { key: "website_feature",        ar: "ميزة في الموقع",             en: "Website feature" },
        { key: "article",                ar: "مقال علمي أو عملي",           en: "Scientific or practical article" },
        { key: "syria_access",           ar: "مشكلة وصول من سوريا أو VPN", en: "Syria access or VPN issue" },
        { key: "partnership_or_community", ar: "تعاون أو فكرة مجتمعية",     en: "Partnership or community idea" },
        { key: "bug_or_improvement",     ar: "مشكلة أو تحسين",              en: "Bug or improvement" },
        { key: "other",                  ar: "أخرى",                        en: "Other" },
      ],
    },
    {
      name: "title",
      type: "text",
      required: true,
      minLength: 3,
      label: {
        ar: "اكتب عنواناً قصيراً لفكرتك",
        en: "Give your idea a short title",
      },
      placeholder: {
        ar: "مثال: فلتر للأدوات التي تعمل مباشرة من سوريا",
        en: "Example: Filter tools that work directly in Syria",
      },
    },
    {
      name: "description",
      type: "textarea",
      required: true,
      minLength: 10,
      rows: 5,
      label: {
        ar: "اشرح الفكرة أو الحاجة",
        en: "Describe the idea or need",
      },
      placeholder: {
        ar: "ما الذي تريد من SAS أن يضيفه أو يغيره أو يشرحه؟",
        en: "What should SAS add, change, or explain?",
      },
    },
    {
      name: "who_helps",
      type: "multiselect",
      required: false,
      label: {
        ar: "من سيستفيد من هذه الفكرة؟",
        en: "Who would this help?",
      },
      // Keys mirror backend _V2_WHO_HELPS.
      options: [
        { key: "students",       ar: "طلاب",                   en: "Students" },
        { key: "researchers",    ar: "باحثون",                  en: "Researchers" },
        { key: "teachers",       ar: "مدرسون",                  en: "Teachers" },
        { key: "developers",     ar: "مطورون",                  en: "Developers" },
        { key: "general_public", ar: "عامة المستخدمين",         en: "General public" },
        { key: "organizations",  ar: "مؤسسات",                  en: "Organizations" },
      ],
    },
    // Contact fields (both optional — anonymous submission allowed).
    {
      name: "contact_name",
      type: "text",
      required: false,
      autoComplete: "name",
      label: {
        ar: "اسمك",
        en: "Your name",
      },
    },
    {
      name: "contact_reach",
      type: "text",
      required: false,
      dirLtr: true,
      label: {
        ar: "بريدك الإلكتروني أو رقم هاتفك",
        en: "Your email or phone",
      },
    },
  ],
};

/* Ordered form catalogue (also used by the Contact hub + admin labels). */
export const ALL_FORMS = [
  PARTNERSHIP_FORM,
  WORKSHOP_FORM,
  GENERAL_CONTACT_FORM,
  VOLUNTEER_FORM,
  SUPPORT_FORM,
  V2_FEEDBACK_FORM,
];

/** Lookup by backend `request_type` key. */
export const getFormByKey = (key) =>
  ALL_FORMS.find((f) => f.key === key) || null;

/** Lookup by URL slug. */
export const getFormBySlug = (slug) =>
  ALL_FORMS.find((f) => f.slug === slug) || null;
